import { FormField, FormItem, FormMessage } from "@/components/ui/form";
import { cn, getTimeForReservation } from "@/lib/utils";
import { RESERVE_TEXTS } from "@/lib/utils-reserve";
import { SaveReservationData } from "@/lib/utils-reserve";
import { Office } from "@gettactic/api";
import { TimeIcon } from "@gettactic/components/src/icons/TacticIcons/TimeIcon";
import { useTimeInputMask } from "@gettactic/helpers/src/input/useTimeInputMask";
import { format } from "date-fns";
import React, { useMemo, useState, useEffect } from "react";
import { FieldErrors, UseFormReturn, useWatch } from "react-hook-form";
import { components } from "react-select";
import AsyncSelect from "react-select/async";
import { inputIcon, inputV2Classes } from "../utils/classes";
import { scrollToSelectedValue } from "../utils/scrollToSelectedValue";
import {
  TimeOption,
  generateTimeOptions,
  loadTimeOptions
} from "./reserveTimeHelpers";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";

type ReserveTimeProps = {
  form: UseFormReturn<any>;
  defaultValue: Date;
  weekDates: Date[];
  setStartTime: React.Dispatch<React.SetStateAction<Date>>;
  office: Office | null;
  errors: FieldErrors<SaveReservationData>;
};

export default function ReserveFieldTime({
  form,
  defaultValue,
  weekDates,
  setStartTime,
  office,
  errors
}: ReserveTimeProps) {
  const baseDate = weekDates[0] || new Date();

  const timeOptions = useMemo(() => {
    const options = generateTimeOptions(baseDate);
    const hasToAddDefaultValue =
      defaultValue &&
      !options.find(
        (option) => option.value.getTime() === defaultValue.getTime()
      );
    if (hasToAddDefaultValue) {
      options.push({
        label: format(defaultValue, "h:mmaaa"),
        value: defaultValue
      });
    }
    return options;
  }, [baseDate, defaultValue]);

  const [selectedOption, setSelectedOption] = useState<TimeOption | null>(null);

  const watchedTime: Date | null =
    useWatch({ control: form.control, name: "time" }) ?? null;

  const optionsWithWatched = useMemo(() => {
    const opts = [...timeOptions];
    if (watchedTime) {
      const exists = opts.some(
        (opt) =>
          opt.value.getHours() === watchedTime.getHours() &&
          opt.value.getMinutes() === watchedTime.getMinutes()
      );
      if (!exists) {
        opts.push({
          label: format(watchedTime, "h:mmaaa"),
          value: watchedTime
        });
      }
    }
    return opts;
  }, [timeOptions, watchedTime]);

  useEffect(() => {
    if (!watchedTime) return;
    const match =
      optionsWithWatched.find(
        (opt) =>
          opt.value.getHours() === watchedTime.getHours() &&
          opt.value.getMinutes() === watchedTime.getMinutes()
      ) ?? null;

    if (
      (match === null && selectedOption !== null) ||
      (match &&
        (!selectedOption ||
          selectedOption.value.getTime() !== match.value.getTime()))
    ) {
      setSelectedOption(match);
    }
  }, [watchedTime, optionsWithWatched, selectedOption]);

  useEffect(() => {
    if (watchedTime) return;
    if (!defaultValue) return;
    const match =
      optionsWithWatched.find(
        (opt) =>
          opt.value.getHours() === defaultValue.getHours() &&
          opt.value.getMinutes() === defaultValue.getMinutes()
      ) ?? null;
    if (
      (match === null && selectedOption !== null) ||
      (match &&
        (!selectedOption ||
          selectedOption.value.getTime() !== match.value.getTime()))
    ) {
      setSelectedOption(match);
    }
  }, [defaultValue, optionsWithWatched, watchedTime, selectedOption]);

  const mask = useTimeInputMask();

  const handleMenuOpen = () => {
    scrollToSelectedValue(selectedOption);
  };

  const isMobileApp = isTacticAppWebView();

  return (
    <>
      <FormField
        control={form.control}
        name="time"
        defaultValue={defaultValue}
        render={({ field }) => (
          <FormItem>
            <div
              className={cn(
                "[.tactic-v2_&]:focus-ring-rainbow reservation-time-select max-h-[42px] flex-1 [&_.react-select__control]:cursor-pointer [&_.react-select__input-container]:col-span-full [&_.react-select__input]:w-full [&_.react-select__value-container]:grid-cols-1",
                isMobileApp && "v2-mobile-app"
              )}
            >
              <AsyncSelect<TimeOption, false>
                key={`time-${field.name}`}
                inputId={field.name}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                onMenuOpen={handleMenuOpen}
                isSearchable={!isMobileApp}
                isClearable={false}
                backspaceRemovesValue={false}
                escapeClearsValue={false}
                components={{
                  Control: (props) => (
                    <components.Control
                      {...props}
                      className={cn("cursor-pointer", props.className)}
                    />
                  ),
                  SingleValue: (props) => (
                    <components.SingleValue {...props}>
                      {props.data.label}
                    </components.SingleValue>
                  ),
                  Input: (props) => (
                    <components.Input
                      className={cn(inputV2Classes)}
                      {...props}
                      size={undefined}
                      {...mask}
                    />
                  ),
                  DropdownIndicator: () => (
                    <div className="flex items-center pr-3">
                      <TimeIcon className={cn(inputIcon)} />
                    </div>
                  ),
                  IndicatorSeparator: () => null
                }}
                onChange={(selectedTimeOption, event) => {
                  if (!weekDates[0]) return;
                  if (selectedTimeOption) {
                    const newTime = getTimeForReservation(
                      weekDates[0],
                      selectedTimeOption.value,
                      office
                    );
                    field.onChange(newTime, event);
                    setStartTime(newTime);
                  }
                }}
                value={selectedOption}
                loadOptions={(filter, callback) =>
                  callback(
                    loadTimeOptions(filter, baseDate, optionsWithWatched)
                  )
                }
                defaultOptions={optionsWithWatched}
                placeholder={RESERVE_TEXTS.selectTime}
                classNamePrefix="react-select"
              />
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      {errors.time && (
        <p className="text-primary">Specify a valid start time</p>
      )}
    </>
  );
}
